/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectManagementWizard/rejectManagementEmailWizard"/>
import { Checkbox, FlexLayout, ResponsiveGridLayout, Switch, TextArea, Text, TextField, IconStatusErrorRegular } from "@medius/ui-controls";  
import { translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import * as React from "react";
import { IEmailSettings } from "./interfaces";
import { getEmailSettings } from "./services";
import { Control, Controller, ControllerRenderProps, FieldValues, FormState, UseFormClearErrors, UseFormHandleSubmit, UseFormReset, UseFormWatch } from "react-hook-form";
import { RejectManagementEmailWizardFormState } from "./useRejectManagementEmailForm";
import { EmailFileAttachment } from "./emailAttachments/emailFileAttachment";

type RejectManagementEmailContext = {
    handleSubmit: UseFormHandleSubmit<FieldValues>;
    control: Control<FieldValues>;
    reset: UseFormReset<FieldValues>;
    defaultState: RejectManagementEmailWizardFormState;
    formState: FormState<FieldValues>;
    watch: UseFormWatch<FieldValues>;
    clearErrors: UseFormClearErrors<FieldValues>
};

type RejectManagementEmailWizardProps = {
    formContext?: RejectManagementEmailContext;
    documentType: string;
    documentId: number;
};

export const RejectManagementEmailWizard = ({
    formContext,
    documentType,
    documentId
}: RejectManagementEmailWizardProps) => {
    const { control, watch, clearErrors } = formContext;
    const sendToSupplier = watch("sendToSupplier");
    const [emailSettings, setEmailSettings] = React.useState<IEmailSettings | null>(null);

    React.useEffect(() => {
        const fetchEmailSettings = async () => {
            const settings = await getEmailSettings();
            setEmailSettings(settings);
        };
        fetchEmailSettings();
    }, []);

    const handleSendToSupplierChange = (e: any, field: ControllerRenderProps) => {
        if(e.value === false)
            clearErrors();
        field.onChange(e.value);
    };

    return (
        <ResponsiveGridLayout 
            maxColumns={1}
            columnWidth={7} 
            >
            <Controller 
                name="emailTo"
                control={control}
                rules={{
                    validate: (value) => !sendToSupplier ? true : value ? true : translate("#Core/defaultValidation_REQUIREDATTRIBUTE")
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        isRequired
                        disabled={!sendToSupplier}
                        fieldLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/emailTo")}
                        errorMessages={fieldState.error && sendToSupplier ? [fieldState.error.message || ""] : []}
                        inputProps={{
                            onChange:(e) => field.onChange(e.target.value),
                            value: field.value
                        }}
                        data-testid="reject-management-wizard-email-to"
                    />
                )}
            />
            <Controller 
                name="sendCopy"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        disabled={!sendToSupplier}
                        label={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/sendMeACopy")}
                        onChange={(e) => field.onChange(e.target.value)}
                        value={field.value}
                        data-testid="reject-management-wizard-send-copy-checkbox"
                    />
                )}
            />
            <Controller 
                name="messageSubject"
                control={control}
                rules={{
                    validate: (value) => !sendToSupplier ? true : value ? true : translate("#Core/defaultValidation_REQUIREDATTRIBUTE")
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        isRequired
                        disabled={!sendToSupplier}
                        fieldLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/messageSubject")}
                        errorMessages={fieldState.error ? [fieldState.error.message || ""] : []}
                        inputProps={{
                            onChange:(e) => field.onChange(e.target.value),
                            value: field.value
                        }}
                        data-testid="reject-management-wizard-message-subject"
                    />
                )}
            />
            <Controller 
                name="content"
                control={control}
                rules={{
                    validate: (value) => !sendToSupplier ? true : value ? true : translate("#Core/defaultValidation_REQUIREDATTRIBUTE")
                }}
                render={({ field, fieldState }) => (
                    // TextAreaField has unremovable label and TextArea does not have default error handling so created it manually
                    <FlexLayout direction="column" spacerMode="off" itemRowSpacing="vsmall">
                        <TextArea
                            width="fill"
                            height="vlarge"
                            onChange={(e) => field.onChange(e.target.value)}
                            value={field.value}
                            disabled={!sendToSupplier}
                            invalid={!!fieldState.error}
                            data-testid="reject-management-wizard-email-content"
                        />
                        {
                            fieldState.error && (
                            <FlexLayout direction="row" itemColumnSpacing="vsmall">
                                <IconStatusErrorRegular color="error"/>
                                <Text variant="small" feedbackType="error">{ fieldState.error.message}</Text>
                            </FlexLayout>
                        )}
                    </FlexLayout>
                )}
            />
            <EmailFileAttachment
                documentType={documentType}
                documentId={documentId}
            />
            <Controller 
                name="sendToSupplier"
                control={control}
                render={({ field }) => (
                    <Switch
                        leftLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/sendEmailToSupplier")}
                        value={!!field.value}
                        onChange={(e) => handleSendToSupplierChange(e, field)}
                        data-testid="reject-management-wizard-send-to-supplier-switch"
                    />
                )}
            />
        </ResponsiveGridLayout>
    );
};